import { useMutation, useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { QueryKey } from '@/Config/api/queryKey';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/Domain/components/Shared/AlertDialog/AlertDialog';
import { Button, IconButton } from '@/Domain/components/Shared/Button/Button';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import type { AIAssistantsData } from '@/Domain/components/TopBar/types';
import { SCENARIO_ID } from '@/context';

export const LeaveOnboardingButton = () => {
  const { t } = useTranslation('getting_started');
  const { data: aiAssistants } = useQuery<AIAssistantsData>({
    queryKey: [QueryKey.aiAssistants.get],
  });
  const [open, setOpen] = useState(false);

  const fallbackAgent = (aiAssistants?.all_ai_assistants ?? []).find(
    (a) => a.scenario_id !== SCENARIO_ID,
  );
  const hasDeleteRole = !!aiAssistants?.current_ai_assistant?.permissions?.delete_ai_agent;

  const { mutate, isPending } = useMutation<void, unknown, void>({
    mutationKey: [QueryKey.implementation.delete],
    onSuccess: () => {
      // Hard navigate so the app shell re-inits against the fallback scenario_id.
      window.location.replace(fallbackAgent ? `/i/${fallbackAgent.scenario_id}` : '/');
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        error?.message ||
        t('leave_onboarding.error');
      toast.error(message);
    },
  });

  if (!fallbackAgent || !hasDeleteRole) return null;

  const triggerLabel = t('leave_onboarding.button');

  return (
    <>
      <IconButton
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => setOpen(true)}
        aria-label={triggerLabel}
        title={triggerLabel}
        className="absolute top-5 right-5 z-20"
        data-testid="gs-leave-onboarding-button"
      >
        <X className="!size-5" />
      </IconButton>
      <AlertDialog open={open} onOpenChange={(next) => !isPending && setOpen(next)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('leave_onboarding.dialog.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('leave_onboarding.dialog.description', { name: fallbackAgent.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel asChild disabled={isPending}>
              <Button variant="secondary" disabled={isPending}>
                {t('leave_onboarding.dialog.cancel')}
              </Button>
            </AlertDialogCancel>
            <Button
              variant="danger"
              disabled={isPending}
              onClick={() => mutate()}
              data-testid="gs-leave-onboarding-confirm"
            >
              {isPending ? (
                <Spinner className="h-4 w-4 text-white" />
              ) : (
                t('leave_onboarding.dialog.confirm')
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
